import { z } from 'zod';
/**
 * The RMiT evidence pack (task 50). Insurers regulated by Bank Negara Malaysia must show, under the Risk
 * Management in Technology policy (RMiT), that technology they outsource is managed to the same standard as
 * their own. The pack answers that for CyberScore: a map of RMiT areas to how CyberScore meets them, with
 * records drawn from the insurer's own tenant (who has access, who approved what, which AI processors saw
 * their data, the audit trail), sealed with a digest.
 *
 * The map names RMiT areas by their headings; the insurer's compliance team checks paragraph numbers
 * against the policy document version they report under.
 */
export const RMIT_AREAS = [
    'Governance',
    'Technology risk management',
    'System development and change',
    'Cryptography',
    'Data centre and cloud resilience',
    'Third-party service providers',
    'Access control',
    'Patch and vulnerability management',
    'Security of digital services',
    'Cybersecurity operations',
    'Data loss prevention',
    'Technology audit',
];
const doc = (ref) => ({ kind: 'doc', ref });
const test = (ref) => ({ kind: 'test', ref });
const pack = (ref) => ({ kind: 'pack', ref });
export const RMIT_CONTROLS = [
    {
        id: 'GOV-1',
        area: 'Governance',
        requirement: 'The board and senior management oversee technology risk, including outsourced technology.',
        how: 'The insurer keeps CyberScore on its outsourcing register and reviews this pack. CyberScore records every decision as an event with who made it.',
        status: 'in_place',
        owner: 'insurer',
        evidence: [pack('audit_trail')],
    },
    {
        id: 'TRM-1',
        area: 'Technology risk management',
        requirement: 'Technology risks are identified, assessed and monitored, with the results reported.',
        how: 'Design decisions are recorded with their risks and alternatives. The internal security review lists findings and open recommendations.',
        status: 'in_place',
        owner: 'shared',
        evidence: [doc('docs/decisions'), doc('docs/security/internal-review-2026-10.md')],
    },
    {
        id: 'CHG-1',
        area: 'System development and change',
        requirement: 'Changes are approved by someone other than the person making them (segregation of duties).',
        how: 'Scoring configuration is published only through releases approved by a second person; a requester cannot approve their own release. Re-scoring a book needs two approvers besides the requester. Approved versions are immutable and hashed.',
        status: 'in_place',
        owner: 'cyberscore',
        evidence: [
            pack('changes'),
            pack('rescores'),
            test('apps/core/test/publishing.e2e.test.ts'),
            doc('docs/decisions/0004-publishing-and-answers.md'),
            doc('docs/decisions/0026-rescoring.md'),
        ],
    },
    {
        id: 'CHG-2',
        area: 'System development and change',
        requirement: 'Software is tested before release, and code changes are reviewed.',
        how: 'Unit, API and browser tests run on every change; database migrations are versioned. Tests run locally today.',
        status: 'partial',
        owner: 'cyberscore',
        gap: 'Continuous integration on GitHub Actions is blocked at the organisation level (t01).',
        evidence: [doc('README.md#checks-same-as-ci')],
    },
    {
        id: 'CRY-1',
        area: 'Cryptography',
        requirement: 'Sensitive data is encrypted at rest and in transit with managed keys.',
        how: 'AI provider keys are encrypted with AES-256-GCM, bound to their record and never returned. The target environment uses KMS-encrypted RDS and S3 and TLS 1.2+ everywhere.',
        status: 'partial',
        owner: 'cyberscore',
        gap: 'Database and storage encryption with KMS waits on the AWS environment (t02).',
        evidence: [
            doc('docs/decisions/0003-model-providers-managed-in-the-app.md'),
            doc('docs/decisions/0001-aws-services-in-ap-southeast-5.md'),
        ],
    },
    {
        id: 'DCR-1',
        area: 'Data centre and cloud resilience',
        requirement: 'Data stays in an approved location; backups are taken and restores are tested.',
        how: 'The target environment is AWS ap-southeast-5 (Malaysia) with automated backups. Evidence files are stored with S3 Object Lock so they cannot be changed or deleted early.',
        status: 'planned',
        owner: 'cyberscore',
        gap: 'Waits on the AWS environment (t02) and the restore test (t51).',
        evidence: [
            doc('docs/decisions/0001-aws-services-in-ap-southeast-5.md'),
            doc('docs/decisions/0017-evidence-uploads.md'),
        ],
    },
    {
        id: 'TPS-1',
        area: 'Third-party service providers',
        requirement: 'The insurer knows which sub-processors handle its data and where.',
        how: 'Every AI call is recorded with the provider and model that served it. The pack lists them for the period. Personal data in documents is masked before it is sent to a model.',
        status: 'in_place',
        owner: 'shared',
        evidence: [pack('ai_processors'), doc('docs/decisions/0003-model-providers-managed-in-the-app.md')],
    },
    {
        id: 'ACC-1',
        area: 'Access control',
        requirement: 'Access follows least privilege and is reviewed periodically, with dormant accounts removed.',
        how: 'Roles are per tenant. Row-level security in the database keeps tenants apart, and SME users see only their own assessments. The pack lists every member with their roles and last sign-in, and flags dormant accounts and risky role combinations.',
        status: 'in_place',
        owner: 'shared',
        evidence: [pack('access'), test('apps/core/test/security.e2e.test.ts')],
    },
    {
        id: 'ACC-2',
        area: 'Access control',
        requirement: 'Strong authentication (multi-factor) for users and privileged access.',
        how: 'Sign-in is delegated to an OpenID Connect provider; tokens are checked for issuer, audience, expiry and signature on every request. Multi-factor authentication is enforced in the identity provider.',
        status: 'partial',
        owner: 'shared',
        gap: 'The identity provider (and with it MFA enforcement) is still to be chosen (t05).',
        evidence: [test('apps/core/test/security.e2e.test.ts')],
    },
    {
        id: 'VUL-1',
        area: 'Patch and vulnerability management',
        requirement: 'Vulnerabilities are found and fixed in time, including in third-party components.',
        how: 'Dependencies are audited (pnpm audit) and pinned. An internal security review was done before the external penetration test.',
        status: 'partial',
        owner: 'cyberscore',
        gap: 'External penetration test waits on a firm and a test environment (t49); pip-audit for the agent service is recommended.',
        evidence: [doc('docs/security/internal-review-2026-10.md'), doc('docs/security/pentest-scope.md')],
    },
    {
        id: 'DIG-1',
        area: 'Security of digital services',
        requirement: 'Internet-facing services resist common attacks and protect sessions.',
        how: 'Every API route needs a token except two public ones; security headers on every response; uploads are type-checked from their bytes and scanned for malware before storage; AI provider addresses cannot reach internal metadata services.',
        status: 'in_place',
        owner: 'cyberscore',
        evidence: [test('apps/core/test/security.e2e.test.ts'), doc('docs/decisions/0017-evidence-uploads.md')],
    },
    {
        id: 'OPS-1',
        area: 'Cybersecurity operations',
        requirement: 'Security events are monitored, and DDoS and abuse are mitigated.',
        how: 'The target environment puts AWS WAF rate limits and Shield in front of the service, with logs to CloudWatch. Per-tenant AI spend is capped in the app.',
        status: 'planned',
        owner: 'cyberscore',
        gap: 'Waits on the AWS environment (t02).',
        evidence: [doc('docs/security/internal-review-2026-10.md')],
    },
    {
        id: 'OPS-2',
        area: 'Cybersecurity operations',
        requirement: 'Incidents affecting the insurer are reported to it promptly so it can meet its own notification duties.',
        how: 'An incident notification clause and contact list are agreed in the service contract.',
        status: 'planned',
        owner: 'shared',
        gap: 'Agreed with the pilot insurer at onboarding (t52).',
        evidence: [],
    },
    {
        id: 'DLP-1',
        area: 'Data loss prevention',
        requirement: 'Data is protected from leaving through unauthorised channels.',
        how: 'Tenants are separated in the database itself. Exports such as this pack are limited to administrators and auditors and are themselves recorded in the audit trail.',
        status: 'in_place',
        owner: 'cyberscore',
        evidence: [pack('audit_trail'), test('apps/core/test/security.e2e.test.ts')],
    },
    {
        id: 'AUD-1',
        area: 'Technology audit',
        requirement: 'A complete, tamper-resistant audit trail is available to auditors.',
        how: 'Every change writes an event in the same transaction; the events table is append-only, even for its owner. Auditors have a read-only role and can export the trail.',
        status: 'in_place',
        owner: 'cyberscore',
        evidence: [pack('audit_trail')],
    },
];
/** A member who has not signed in for this long is flagged as dormant. */
export const DORMANT_DAYS = 90;
/** The longest period one pack covers. */
export const PACK_MAX_DAYS = 366;
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
export const EvidencePackQuery = z
    .strictObject({ from: Day.optional(), to: Day.optional() })
    .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: '"from" must not be after "to"',
    path: ['from'],
})
    .refine((q) => !q.from || !q.to || daysBetween(q.from, q.to) <= PACK_MAX_DAYS, {
    message: `A pack covers at most ${PACK_MAX_DAYS} days`,
    path: ['to'],
});
export function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
/** The period a pack covers: as asked, or the 90 days up to `today`. */
export function packPeriod(q, today) {
    const to = q.to ?? today;
    const from = q.from ?? new Date(Date.parse(`${to}T00:00:00Z`) - 89 * 86_400_000).toISOString().slice(0, 10);
    return { from, to };
}
/** What an access review should look at: dormant accounts and role combinations that weaken checks. */
export function accessFindings(members, now) {
    const out = [];
    for (const m of members) {
        const who = m.email ?? m.name ?? m.user_id;
        if (m.status === 'suspended') {
            out.push({ user_id: m.user_id, kind: 'suspended', message: `${who} is suspended; remove the membership if it is no longer needed.` }); // prettier-ignore
            continue;
        }
        const idle = Math.floor((now.getTime() - Date.parse(m.last_seen_at)) / 86_400_000);
        if (idle >= DORMANT_DAYS)
            out.push({
                user_id: m.user_id,
                kind: 'dormant',
                message: `${who} has not signed in for ${idle} days.`,
            });
        if (m.roles.includes('config_editor') && m.roles.includes('config_approver'))
            out.push({ user_id: m.user_id, kind: 'maker_and_checker', message: `${who} can both draft and approve configuration (their own releases are still refused).` }); // prettier-ignore
        if (m.roles.includes('auditor') && m.roles.some((r) => r !== 'auditor'))
            out.push({ user_id: m.user_id, kind: 'admin_and_auditor', message: `${who} audits a tenant in which they also act (${m.roles.filter((r) => r !== 'auditor').join(', ')}).` }); // prettier-ignore
    }
    return out;
}
export function coverage(controls) {
    const out = { in_place: 0, partial: 0, planned: 0, total: controls.length };
    for (const c of controls)
        out[c.status] += 1;
    return out;
}
/**
 * One CSV cell. Quotes when needed, and defuses values a spreadsheet would run as a formula (=, +, -, @,
 * tab, carriage return) by prefixing an apostrophe: audit trails carry text people typed.
 */
export function csvCell(v) {
    if (v === null || v === undefined)
        return '';
    let s = typeof v === 'string' ? v : typeof v === 'object' ? JSON.stringify(v) : String(v);
    if (typeof v === 'string' && /^[=+\-@\t\r]/.test(s))
        s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
export function toCsv(columns, rows) {
    const lines = [columns.join(','), ...rows.map((r) => columns.map((c) => csvCell(r[c])).join(','))];
    return `${lines.join('\r\n')}\r\n`;
}
export const AUDIT_COLUMNS = ['id', 'occurred_at', 'type', 'aggregate_type', 'aggregate_id', 'actor_type', 'actor', 'payload']; // prettier-ignore
//# sourceMappingURL=evidence-pack.js.map